import {HttpErrorResponse} from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, computed, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {PersonLogin} from '../person/person-login';
import {PersonLoginService} from '../services/person-login-service';
import {SfrManagementService} from '../services/sfr-management-service';
import {StringUtils} from '../utils/string-utils';
import {SfrEntity} from './sfr-entity';
import {SfrEntityLine} from './sfr-entity-line';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {EntityService} from "../services/entity-service";
import {Entity} from "../entity/entity";
import {DialogService} from "../services/dialog.service";

@Component({
  selector: 'sfr-management',
  templateUrl: './sfr-management.component.html',
  styleUrls: ['./sfr-management.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner, MatButton, MatIcon, FormsModule]
})
export class SfrManagementComponent implements OnInit, OnChanges {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly dialogService = inject(DialogService);
  private readonly entityService = inject(EntityService);
  private readonly sfrService = inject(SfrManagementService);
  private readonly router = inject(Router);
  private personLogin!: PersonLogin;
  private readonly originalEntity = signal<SfrEntity>(undefined!);
  private readonly salesEntity = signal<Entity>(undefined!);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly revision = computed(() => +this.entityRevision());
  readonly sfrEntity = signal<SfrEntity>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  sfrAddText!: string;
  selectedTextEntry!: string;
  filteredLines!: SfrEntityLine[];
  lineFilter!: string;
  readonly noChanges = signal<boolean>(true);

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    if (StringUtils.isBlank(this.entityId()) || StringUtils.isEmptyNumericValue(this.revision())) {
      this.backToMain();
    } else {
      this.loadEntityData();
    }
  }

  private loadEntityData(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.sfrService.getSfrEntity(this.entityId(), this.revision()).subscribe({
      next: (sfrEntity: SfrEntity) => {
        this.originalEntity.set(JSON.parse(JSON.stringify(sfrEntity)));
        this.sfrEntity.set(sfrEntity);
        this.entityService.getEntityByKey(sfrEntity.entityKey).subscribe({
          next: (entity: Entity) => {
            this.salesEntity.set(entity);
            this.noChanges.set(true);
            this.isBusy.set(false);
          }, error: (error: any) => this.handleError(error)
        });
      }, error: (error: any) => this.handleError(error)
    });
  }

  addLineCount(textEntry: string): string {
    const affectedLineCount: number = this.getAffectedLineCount(textEntry);
    if (affectedLineCount === 0) {
      return '(0 lines selected) ' + textEntry;
    }
    const plural: string = affectedLineCount === 1 ? 'line' : 'lines';
    return '(' + affectedLineCount + ' ' + plural + ' selected) ' + textEntry;
  }

  private getAffectedLineCount(textEntry: string): number {
    let count = 0;
    for (const line of this.sfrEntity().lines) {
      if ((line.sfrTextEntries?.length || 0) > 0 && line.sfrTextEntries.indexOf(textEntry) >= 0) {
        count += 1;
      }
    }
    return count;
  }

  canSubmitSfrs(): boolean {
    if (StringUtils.isNotBlank(this.sfrEntity()?.updatePreventionMessage)) {
      return false;
    }
    if (this.noChanges()) {
      return false;
    }
    if (this.isQuote()) {
      const applicableCodes: string[] = ['New', 'SFR Approved'];
      return this.salesEntity().updateAllowed && this.quoteStatusIn(applicableCodes);
    } else if (this.isOrder()) {
      return this.salesEntity().updateAllowed && this.isBlanket();
    }
    return false;
  }

  submitSfrs(): void {
    if (this.canSubmitSfrs()) {
      this.errorMessage.set(undefined!);
      this.isBusy.set(true);
      const orphanTextEntries: string[] = this.getOrphanTextEntries();
      if (orphanTextEntries.length > 0) {
        const messages: string[] = this.getOrphanTextMessages(orphanTextEntries);
        this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
          next: (proceed: boolean) => {
            if (proceed) {
              this.submitAndReload();
            } else {
              this.isBusy.set(false);
            }
          }, error: (error: any) => this.handleError(error)
        });
      } else {
        this.submitAndReload();
      }
    }
  }

  private submitAndReload(): void {
    this.sfrService.save(this.sfrEntity()).subscribe({
      next: () => {
        this.sfrService.createRequest(this.sfrEntity().entityKey).subscribe({
          next: () => this.loadEntityData(),
          error: (error: any) => this.handleError(error)
        });
      }, error: (error: any) => this.handleError(error)
    });
  }

  private isBlanket(): boolean {
    const entityId: string = this.salesEntity()?.id?.entityId || '';
    return entityId.startsWith('MR') || entityId.startsWith('SR');
  }

  private quoteStatusIn(codes: string[]): boolean {
    const statusDesccription: string = this.salesEntity()?.statusDescription || '';
    return codes.indexOf(statusDesccription) >= 0;
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  backToEntity(): void {
    const pageName: string = this.isQuote() ? "quotes" : "orders";
    this.router.navigate([pageName, this.sfrEntity().entityKey.entityId, this.sfrEntity().entityKey.entityRevision]).then(() => this.isBusy.set(false));
  }

  save(): void {
    this.resetStatus();
    const orphanTextEntries: string[] = this.getOrphanTextEntries();
    if (orphanTextEntries.length > 0) {
      const messages: string[] = this.getOrphanTextMessages(orphanTextEntries);
      this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
        next: (proceed: boolean) => {
          if (proceed) {
            this.saveAndReload();
          } else {
            this.isBusy.set(false);
          }
        }, error: (error: any) => this.handleError(error)
      });
    } else {
      this.saveAndReload();
    }
  }

  private getOrphanTextMessages(orphans: string[]): string[] {
    const entries: string[] = [];
    entries.push('Text entries that are not associated with any lines will be lost.');
    entries.push('This includes entries:');
    entries.push('');
    for (const orphan of orphans) {
      entries.push(orphan);
    }
    entries.push('');
    entries.push('Do you want to proceed and lose these text entries?');
    return entries;
  }

  private getOrphanTextEntries(): string[] {
    const orphans: string[] = [];
    for (const entry of this.sfrEntity().sfrTextEntries) {
      const lineCount: number = this.getAffectedLineCount(entry);
      if (lineCount === 0) {
        if (entry.length > 60) {
          orphans.push(entry.substring(0, 60) + '...');
        } else {
          orphans.push(entry);
        }
      }
    }
    return orphans;
  }

  private saveAndReload(): void {
    this.sfrService.save(this.sfrEntity()).subscribe({
      next: () => this.loadEntityData(),
      error: (error: any) => this.handleError(error)
    });
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
  }

  private backToMain(): void {
    this.router.navigate(['main']).then(() => this.isBusy.set(false));
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']).then(() => this.isBusy.set(false));
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.initialize();
    }
  }

  getEntityName(): string {
    if (this.isQuote()) {
      return 'Quote';
    } else if (this.isOrder()) {
      return 'Order';
    }
    return '';
  }

  private isOrder(): boolean {
    const orderNames: string[] = ['CONTRACT', 'ORDER'];
    const entityName: string = this.sfrEntity()?.entityKey?.entityName || '';
    return orderNames.indexOf(entityName) >= 0;
  }

  private isQuote(): boolean {
    return (this.sfrEntity()?.entityKey?.entityName || '') === 'QUOTE';
  }

  addTextEntry(): void {
    if (StringUtils.isNotBlank(this.sfrAddText)) {
      let textToSelect: string = this.sfrAddText;
      const existingEntry: string = this.findEntryInList(this.sfrAddText, this.sfrEntity().sfrTextEntries);
      if (existingEntry) {
        textToSelect = existingEntry;
      } else {
        this.sfrEntity().sfrTextEntries.push(this.sfrAddText);
      }
      this.selectTextEntry(textToSelect);
      this.noChanges.set(!this.hasInformationChanged());
      this.sfrAddText = undefined!;
    }
  }

  private hasInformationChanged(): boolean {
    if (this.originalEntity() && this.sfrEntity()) {
      let textChanged = this.isTextDifferent(this.originalEntity().sfrTextEntries, this.sfrEntity().sfrTextEntries);
      if (textChanged) {
        return true;
      }
      const originalLineCount = this.originalEntity().lines && this.originalEntity().lines.length > 0 ? this.originalEntity().lines.length : 0;
      const currentLineCount = this.sfrEntity().lines && this.sfrEntity().lines.length > 0 ? this.sfrEntity().lines.length : 0;
      if (originalLineCount !== currentLineCount) {
        return true;
      } else {
        for (const originalLine of this.originalEntity().lines) {
          const filteredLines = this.sfrEntity().lines.filter(line => line.lineId === originalLine.lineId);
          if (filteredLines.length === 1) {
            const currentLine = filteredLines[0];
            textChanged = this.isTextDifferent(originalLine.sfrTextEntries, currentLine.sfrTextEntries);
            if (textChanged) {
              return true;
            }
          } else {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isTextDifferent(first: string[], second: string[]): boolean {
    const firstCount = first && first.length > 0 ? first.length : 0;
    const secondCount = second && second.length > 0 ? second.length : 0;
    if (firstCount !== secondCount) {
      return true;
    } else {
      for (const entry of first) {
        if (second.indexOf(entry) < 0) {
          return true;
        }
      }
      for (const entry of second) {
        if (first.indexOf(entry) < 0) {
          return true;
        }
      }
    }
    return false;
  }

  private findEntryInList(search: string, list: string[]): string {
    if (!search || search.length === 0 || !list || list.length === 0) {
      return null!;
    }
    const existingTextEntries = list.filter(entry => entry.replace(/\s/g, '').toLowerCase() === search.replace(/\s/g, '').toLowerCase());
    return existingTextEntries.length === 0 ? null! : existingTextEntries[0];
  }

  selectTextEntry(entry: string): void {
    this.selectedTextEntry = entry;
    this.filterSfrLines();
  }

  filterSfrLines(): void {
    this.filteredLines = this.sfrEntity().lines;
    if (this.lineFilter && this.lineFilter.trim().length > 0) {
      this.filteredLines = this.filteredLines.filter(line => this.filterMatches(line.productId) || this.filterMatches(line.description.replace(/_/g, '')) || this.filterMatches(line.tags));
    }
    for (const line of this.filteredLines) {
      const entry = this.findEntryInList(this.selectedTextEntry, line.sfrTextEntries);
      line.selected = !!(entry && entry.length > 0);
    }
  }

  private filterMatches(property: string) {
    if (!property || property.length === 0) {
      return false;
    } else if (!this.lineFilter || this.lineFilter.trim().length === 0) {
      return true;
    } else {
      this.lineFilter = this.lineFilter.trim();
      return property.toLowerCase().indexOf(this.lineFilter.toLowerCase(), 0) > -1;
    }
  }

  toggleAllSelections(): void {
    if (this.allItemsSelected()) {
      for (const entry of this.filteredLines) {
        this.updateTextSelection(entry);
      }
    } else {
      const unselected = this.filteredLines.filter(line => !line.selected);
      for (const entry of unselected) {
        this.updateTextSelection(entry);
      }
    }
  }

  allItemsSelected(): boolean {
    if (this.filteredLines.length === 0) {
      return true;
    } else {
      const unselected = this.filteredLines.filter(line => !line.selected);
      return unselected.length === 0;
    }
  }

  updateTextSelection(line: SfrEntityLine): void {
    if (!line.sfrTextEntries) {
      line.sfrTextEntries = [];
    }
    const entry = this.findEntryInList(this.selectedTextEntry, line.sfrTextEntries);
    if (entry && entry.length > 0) {
      const entryIndex: number = line.sfrTextEntries.indexOf(entry, 0);
      line.sfrTextEntries.splice(entryIndex, 1);
    } else {
      line.sfrTextEntries.push(this.selectedTextEntry);
    }
    line.selected = !entry || entry.length === 0;
    this.noChanges.set(!this.hasInformationChanged());
  }

}
